import { Construction, type LucideIcon } from 'lucide-react';
import { NeuCard } from '@/components/ui/neu-card';

interface PlaceholderPageProps {
  title: string;
  description?: string;
  icon?: LucideIcon;
  /** Optional list of features/capabilities this module will include */
  features?: string[];
}

export function PlaceholderPage({
  title,
  description = 'Este modulo se encuentra en desarrollo y estara disponible proximamente.',
  icon: Icon = Construction,
  features,
}: PlaceholderPageProps) {
  return (
    <div className="max-w-[700px] mx-auto px-4 md:px-6 lg:px-0 py-8 md:py-12">
      <NeuCard className="flex flex-col items-center text-center">
        {/* Icon container with neumorphic inset */}
        <div
          className="flex items-center justify-center w-24 h-24 rounded-2xl mb-6"
          style={{
            boxShadow:
              'inset 4px 4px 8px var(--sime-shadow-dark), inset -4px -4px 8px var(--sime-shadow-light)',
          }}
        >
          <Icon className="h-12 w-12 text-sime-accent" />
        </div>

        {/* Title */}
        <h1 className="text-2xl md:text-[28px] font-bold text-sime-text-primary mb-3 tracking-tight-md">
          {title}
        </h1>

        {/* Badge "En desarrollo" */}
        <span
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold mb-5"
          style={{
            boxShadow:
              'inset 2px 2px 4px var(--sime-shadow-dark), inset -2px -2px 4px var(--sime-shadow-light)',
            color: 'var(--sime-accent)',
          }}
        >
          <Construction className="h-3.5 w-3.5" />
          En desarrollo
        </span>

        {/* Description */}
        <p className="text-sime-text-secondary text-sm md:text-base max-w-lg leading-relaxed mb-6">
          {description}
        </p>

        {/* Features list */}
        {features && features.length > 0 && (
          <div
            className="w-full rounded-xl p-4 text-left"
            style={{
              boxShadow:
                'inset 3px 3px 6px var(--sime-shadow-dark), inset -3px -3px 6px var(--sime-shadow-light)',
            }}
          >
            <p className="text-xs font-semibold text-sime-text-tertiary uppercase tracking-wider mb-3">
              Funcionalidades planificadas
            </p>
            <ul className="space-y-2">
              {features.map((feature) => (
                <li key={feature} className="flex items-start gap-2 text-sm text-sime-text-secondary">
                  <span className="mt-1 w-1.5 h-1.5 rounded-full bg-sime-accent shrink-0" />
                  {feature}
                </li>
              ))}
            </ul>
          </div>
        )}
      </NeuCard>
    </div>
  );
}
